import _isEqual from "lodash-es/isEqual";
import { Suspense, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Role } from "services/rbac/types";

import ErrorMessage from "pages/DevelopWorkspace/Editor/Document/DocumentOutput/DocumentResults/ErrorMessage/ErrorMessage";
import { useViewOnly } from "pages/govern/Roles/useViewOnlyContext";

import { SimpleErrorBoundary } from "components/ErrorBoundary/SimpleErrorBoundary";
import LoadingOverlap from "components/LoadingOverlap";
import { Sidebar } from "components/Wizard/Sidebar/Sidebar";
import { Step } from "components/Wizard/Step/Step";
import { Wizard } from "components/Wizard/Wizard";
import { useActiveStep } from "components/Wizard/useActiveStep";
import { useCloseConfirm } from "components/Wizard/useCloseConfirm";

import { stepsByType } from "./steps";
import { RolesDataType, RolesWizardStep } from "./types";
import { useDatabasePrivilegesInEdit } from "./useDatabasePrivilegesInEdit";
import { usePrivileges } from "./usePrivileges";
import { PrivilegesContext } from "./usePrivilegesContext";
import { getPrivilegesFromRole } from "./utils";

import styles from "./styles.module.scss";

const createSteps = [
  {
    id: RolesWizardStep.name,
    title: "roles_wizard.name.title",
  },
  {
    id: RolesWizardStep.privileges,
    title: "roles_wizard.privileges.title",
  },
  {
    id: RolesWizardStep.assign,
    title: "roles_wizard.assign.step_title",
  },
];

const initialData = {
  [RolesWizardStep.name]: {
    name: "",
  },
  [RolesWizardStep.privileges]: {
    privileges: null,
  },
  [RolesWizardStep.editPrivileges]: {
    privileges: null,
  },
  [RolesWizardStep.editDatabasePrivileges]: {
    privileges: null,
  },
  [RolesWizardStep.assign]: {
    grantee: null,
  },
};

const getStateFromRole = (role: Role) => {
  const privileges = getPrivilegesFromRole(role);
  const data = {
    privileges: {
      ...privileges,
      databasePrivileges: {},
      permissionErrors: {
        table: [],
        view: [],
        schema: [],
      },
    },
  };

  return {
    [RolesWizardStep.name]: {
      name: role.roleName,
    },
    [RolesWizardStep.privileges]: data,
    [RolesWizardStep.editPrivileges]: data,
    [RolesWizardStep.editDatabasePrivileges]: data,
    [RolesWizardStep.assign]: {
      grantee: role.grantees,
    },
  };
};

const getRelatedStep = (step: RolesWizardStep) => {
  if (
    [
      RolesWizardStep.editPrivileges,
      RolesWizardStep.editDatabasePrivileges,
    ].includes(step)
  ) {
    return RolesWizardStep.editPrivileges;
  }
  return step;
};

export const RolesWizard = (props: {
  onClose: () => void;
  onFinished: (rolesData: RolesDataType, selectedUsers: string[]) => void;
  disableNameChange?: boolean;
  role?: Role;
  isLoading?: boolean;
}) => {
  const { onClose, onFinished, disableNameChange, role, isLoading } = props;
  const { t } = useTranslation();
  const databasePrivilegesInEdit = useDatabasePrivilegesInEdit();

  const isViewOnly = useViewOnly();
  const [rolesData, setRolesData] = useState<RolesDataType>(
    role ? getStateFromRole(role) : initialData
  );

  const editSteps = [
    {
      id: RolesWizardStep.name,
      title: "roles_wizard.name.title",
    },
    {
      id: RolesWizardStep.editPrivileges,
      title: "roles_wizard.privileges.title",
    },
    ...(databasePrivilegesInEdit
      ? []
      : [
          {
            id: RolesWizardStep.editDatabasePrivileges,
            title: "roles_wizard.database_privileges.title",
          },
        ]),
    {
      id: RolesWizardStep.assign,
      title: "roles_wizard.assign.step_title",
    },
  ];
  const steps = role ? editSteps : createSteps;

  const {
    activeStepIndex,
    activeStep,
    handleNextStep,
    handlePreviousStep,
    handleSelectStep,
  } = useActiveStep<RolesWizardStep>({
    onNextStep: (data: any) => {
      setRolesData(wizardData => ({
        ...wizardData,
        [getRelatedStep(activeStep)]: data,
      }));
    },
    initialStep: RolesWizardStep.name,
    steps,
    role,
  });

  const {
    state: privilegesState,
    dispatch: privilegesDispatch,
    getDiff,
    validateEmptySections,
  } = usePrivileges(rolesData[RolesWizardStep.editPrivileges].privileges);

  const context = useMemo(() => {
    return {
      privilegesDispatch,
      privilegesState,
      getDiff,
      validateEmptySections,
    };
  }, [privilegesState, privilegesDispatch, getDiff, validateEmptySections]);

  const isDirty = !_isEqual(rolesData, initialData);

  const { confirmationMarkup, openConfirmation } = useCloseConfirm({
    onClose,
    isDirty,
    confirmationModalMessage: t("roles_wizard.close_confirmation.message"),
    confirmationModalTitle: t("roles_wizard.close_confirmation.title"),
    blockerMessage: t("roles_wizard.blocker.message"),
    blockerTitle: t("roles_wizard.blocker.title"),
  });

  const handleClose = () => {
    if (isDirty) {
      openConfirmation();
    } else {
      onClose();
    }
  };

  const StepComponent: React.ComponentType<any> = useMemo(
    () => stepsByType[activeStep],
    [activeStep]
  );

  const title = (() => {
    if (isViewOnly) {
      return t("roles_wizard.title_view");
    }
    if (role) {
      return t("roles_wizard.title_edit");
    }
    return t("roles_wizard.title");
  })();

  return (
    <PrivilegesContext.Provider value={context}>
      <Wizard
        testId="roles-wizard"
        onClose={handleClose}
        title={title}
        maxWidth="lg"
        size="lg"
        body={
          <>
            <Sidebar<RolesWizardStep>
              activeStep={activeStep}
              activeStepIndex={activeStepIndex}
              handleSelectStep={handleSelectStep}
              steps={steps}
            />
            <div className={styles.content}>
              <SimpleErrorBoundary
                renderError={error => (
                  <Step
                    title={title}
                    subtitle={null}
                    body={<ErrorMessage errorMessage={error.message} />}
                    bodyClassName={styles.errorBody}
                    onClose={handleClose}
                    onSubmit={() => {}}
                    activeStepIndex={activeStepIndex}
                    onPrevStep={handlePreviousStep}
                    disabledSubmit
                  />
                )}
                key={`step-component-${activeStep}`}
              >
                <Suspense fallback={<LoadingOverlap isLoading />}>
                  <StepComponent
                    onClose={handleClose}
                    onFinished={onFinished}
                    onSubmit={handleNextStep}
                    onPrevStep={handlePreviousStep}
                    initialData={rolesData[getRelatedStep(activeStep)]}
                    rolesData={rolesData}
                    activeStepIndex={activeStepIndex}
                    disableNameChange={disableNameChange}
                    isLoading={isLoading}
                    role={role}
                  />
                </Suspense>
              </SimpleErrorBoundary>
            </div>
          </>
        }
      />
      {confirmationMarkup}
    </PrivilegesContext.Provider>
  );
};

export default RolesWizard;
